import { Cron } from "croner";
import {
  addDays,
  addHours,
  formatDuration,
  intervalToDuration,
} from "date-fns";
import pluralize from "pluralize";
import {
  type Schedule,
  ScheduleType,
  type VisualCronSchedule,
} from "src/components/schedule/types";
import {
  selectedVisualCronDays,
  type VisualCronDay,
  visualCronToCronExpressions,
} from "src/components/schedule/visual-cron";
import { FormkitModel } from "src/formkit/components/formkit-context";
import {
  ModelWithoutColumnsQuery,
  ObjectQuery,
  SyncQuery,
} from "src/graphql/types";
import { isSyncMatchBoosted } from "src/pages/syncs/sync/matchbooster";

export function formatDurationDisplay({
  start,
  end,
}: {
  start: number | Date;
  end: number | Date;
}) {
  return overrideDateTimeUnits(
    formatDuration(
      intervalToDuration({
        start,
        end,
      }),
      { zero: false, format: ["days", "hours", "minutes"] },
    ),
    { hour: "hr", minute: "min" },
  );
}

// override date-fn formattedDuration specific units spelling
function overrideDateTimeUnits(
  formattedDuration: string,
  unitFormats: { hour: string; minute: string },
) {
  return formattedDuration
    .replace(/\bhour(s?)\b/g, `${unitFormats.hour}$1`)
    .replace(/\bminute(s?)\b/g, `${unitFormats.minute}$1`);
}

const VISUAL_CRON_DAY_LABEL: Record<VisualCronDay, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

function nextCronRun(expression: string, from: Date): Date | null {
  try {
    return Cron(expression.trim(), { utcOffset: 0 }).nextRun(from) ?? null;
  } catch {
    return null;
  }
}

function nextVisualCronRun(
  schedule: VisualCronSchedule["schedule"],
  from: Date,
): Date | null {
  const runs = visualCronToCronExpressions(schedule?.expressions ?? [])
    .map((expression) => nextCronRun(expression, from))
    .filter((run): run is Date => run !== null);
  if (runs.length === 0) return null;
  return runs.reduce((earliest, run) => (run < earliest ? run : earliest));
}

export function getNextScheduledEnrichment(
  enrichmentSchedule: Schedule | undefined,
  lastEnrichedAt: string | undefined,
): Date | null {
  if (!lastEnrichedAt) return null;
  const from = new Date(lastEnrichedAt);

  switch (enrichmentSchedule?.type) {
    case ScheduleType.INTERVAL: {
      const interval = enrichmentSchedule.schedule?.interval;
      if (!interval?.quantity) return null;
      switch (interval.unit) {
        case "hour":
          return addHours(from, interval.quantity);
        case "day":
          return addDays(from, interval.quantity);
        case "week":
          return addDays(from, interval.quantity * 7);
        default:
          return addDays(from, 14);
      }
    }
    case ScheduleType.CRON:
      return enrichmentSchedule.schedule?.expression
        ? nextCronRun(enrichmentSchedule.schedule.expression, from)
        : null;
    case ScheduleType.CUSTOM:
      return nextVisualCronRun(enrichmentSchedule.schedule, from);
    default:
      return null;
  }
}

/**
 * Human-readable summary of an enrichment schedule for the status card.
 *
 * Examples by schedule type:
 *  - Cron:        `Cron: 0 9 * * 1`
 *  - VisualCron:  `Custom: Mon, Wed at 09:00 UTC` (multiple rows joined with
 *                 "; ", e.g. `Custom: Mon at 09:00 UTC; Fri at 17:00 UTC`)
 *  - Interval:    `Every day`, `Every three days`, `Every two weeks`
 *  - None/unknown: `Every day` (mirrors the scheduler's daily-interval default)
 */
export function getEnrichmentSchedule(
  enrichmentSchedule: Schedule | undefined,
): string {
  switch (enrichmentSchedule?.type) {
    case ScheduleType.CRON: {
      const expression = enrichmentSchedule.schedule?.expression;
      return expression ? `Cron: ${expression}` : "Custom schedule";
    }
    case ScheduleType.CUSTOM: {
      const summary = (enrichmentSchedule.schedule?.expressions ?? [])
        .map((expression) => {
          const days = selectedVisualCronDays(expression).map(
            (day) => VISUAL_CRON_DAY_LABEL[day],
          );
          if (days.length === 0) return null;
          return `${days.join(", ")}${
            expression.time ? ` at ${expression.time} UTC` : ""
          }`;
        })
        .filter(Boolean);
      return summary.length > 0
        ? `Custom: ${summary.join("; ")}`
        : "Custom recurrence";
    }
    case ScheduleType.INTERVAL: {
      const interval = enrichmentSchedule.schedule?.interval;
      if (!interval?.quantity || !interval.unit) return "Every day";
      return `Every ${numberAsWords(interval.quantity)} ${pluralize(
        interval.unit,
        interval.quantity,
      )}`;
    }
    // No schedule configured (or an unhandled type): the scheduler defaults to
    // a daily interval, so mirror that here.
    default:
      return "Every day";
  }
}

function numberAsWords(num: number): string | number {
  if (num === 1) return "one";
  if (num === 2) return "two";
  if (num === 3) return "three";
  if (num === 4) return "four";
  if (num === 5) return "five";
  if (num === 6) return "six";
  if (num === 7) return "seven";
  if (num === 8) return "eight";
  if (num === 9) return "nine";
  if (num === 10) return "ten";
  return num;
}

export function doesModelContainBoostedSyncs(
  model:
    | ModelWithoutColumnsQuery["segments_by_pk"]
    | ObjectQuery["segments_by_pk"]
    | null
    | undefined,
) {
  // Assert as ModelQuery to check for syncs property. Audience's parent model
  // does not return the syncs property so this will always be false
  const syncs =
    (model as ModelWithoutColumnsQuery["segments_by_pk"])?.syncs ?? [];
  if (!model || !syncs.length) return false;

  for (const sync of syncs) {
    if (isSyncMatchBoosted(sync.config)) {
      return true;
    }
  }

  return false;
}

export type IdentifierField = {
  id: string;
  columnName: string;
  identifier: string | null;
  // For merge columns (Audience models only), stores the column reference
  columnReference?: unknown;
};

// Hashed and plaintext versions of the same name/address field are mutually
// exclusive: matching runs against one representation per field, so mapping
// both would be redundant at best and contradictory at worst. Email/phone
// pairs are intentionally absent; those may map together (existing behavior).
const HASHED_IDENTIFIER_PAIRS: [string, string][] = [
  ["first_name", "first_name_sha256"],
  ["last_name", "last_name_sha256"],
  ["street", "street_sha256"],
  ["street2", "street2_sha256"],
  ["city", "city_sha256"],
  ["state", "state_sha256"],
  ["zip", "zip_sha256"],
];

export const EXCLUSIVE_IDENTIFIER_SIBLINGS: Record<string, string> =
  Object.fromEntries(
    HASHED_IDENTIFIER_PAIRS.flatMap(([plaintext, hashed]) => [
      [plaintext, hashed],
      [hashed, plaintext],
    ]),
  );

export function containsModelColumn(fields: IdentifierField[]): boolean {
  if (!fields.length) return false;

  return fields.some((f) => f.columnName != null);
}

export function getModelToBoostLink(
  model: SyncQuery["syncs"][0]["segment"] | FormkitModel | null | undefined,
): string {
  if (!model || !model?.id) return "";

  const { id, visual_query_parent_id, connection } = model;

  if (visual_query_parent_id) {
    if (!connection?.id) return "";

    return `/schema-v2/view/match-booster?source=${connection.id}&id=${visual_query_parent_id}`;
  }

  return `/models/${id}/match-booster`;
}

export function isConversionSyncType(
  configType: string | undefined,
  destinationType: string | undefined,
) {
  if (!configType) return false;

  // Meta CAPI is its own destination
  if (destinationType === "facebookConversions") return true;

  return [
    // TikTok
    "event",
    "offline-event",
    // GAds
    "ClickConversions",
    "OfflineStoreConversions",
  ].includes(configType);
}

type DestinationMetadata =
  | { status: string | undefined; [x: string]: unknown }
  | null
  | undefined;

export function isAudienceStatusProcessing(metadata: DestinationMetadata) {
  return metadata?.status === "processing";
}

/**
 * If the destination doesn't provide us an explicit processing state, we assume
 * the matched count can still be settling if it's been under 72 hours since the
 * first sync run ran (based on our own testing) so we can't trust this number yet.
 */
export function isMatchedUsersCountSettling(
  syncFirstRunAt: string | null | undefined,
  metadata: DestinationMetadata,
  useExistingSegment: boolean | undefined,
) {
  if (
    !syncFirstRunAt ||
    isAudienceStatusProcessing(metadata) ||
    useExistingSegment
  )
    return false;

  return Date.now() - new Date(syncFirstRunAt).getTime() < 72 * 60 * 60 * 1000;
}
